import { useEffect, useRef, useState, useCallback } from 'react';

interface PosterModalProps {
  inviteCode: string;
  nickname: string;
  onClose: () => void;
}

const POSTER_W = 750;
const POSTER_H = 1200;

const PosterModal = ({ inviteCode, nickname, onClose }: PosterModalProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [generating, setGenerating] = useState(true);
  const [posterUrl, setPosterUrl] = useState('');
  const [saveHint, setSaveHint] = useState(false);

  const drawRoundRect = (
    ctx: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number, r: number
  ) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  };

  const drawPoster = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = POSTER_W;
    canvas.height = POSTER_H;

    // ── 背景渐变 ──
    const bgGrad = ctx.createLinearGradient(0, 0, 0, POSTER_H);
    bgGrad.addColorStop(0, '#064e3b');
    bgGrad.addColorStop(0.45, '#065f46');
    bgGrad.addColorStop(1, '#022c22');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, POSTER_W, POSTER_H);

    // ── 装饰圆圈（背景纹理）──
    const circles = [
      { x: -60, y: -60, r: 220, alpha: 0.06 },
      { x: POSTER_W + 60, y: 180, r: 280, alpha: 0.05 },
      { x: 80, y: POSTER_H - 100, r: 200, alpha: 0.06 },
      { x: POSTER_W - 80, y: POSTER_H - 200, r: 260, alpha: 0.04 },
      { x: POSTER_W / 2, y: 420, r: 350, alpha: 0.03 },
    ];
    circles.forEach(({ x, y, r, alpha }) => {
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,255,${alpha})`;
      ctx.fill();
    });

    // ── 顶部 Logo 区域 ──
    ctx.fillStyle = 'rgba(255,255,255,0.08)';
    drawRoundRect(ctx, 60, 60, 630, 90, 20);
    ctx.fill();

    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.font = 'bold 36px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('法 之 源', POSTER_W / 2, 116);

    // ── 主标题 ──
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 64px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('邀请您加入', POSTER_W / 2, 240);

    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.font = '32px sans-serif';
    ctx.fillText('一起开启健康美丽之旅', POSTER_W / 2, 295);

    // ── 产品图片区域（渐变装饰块）──
    const imgGrad = ctx.createLinearGradient(0, 330, 0, 620);
    imgGrad.addColorStop(0, 'rgba(255,255,255,0.12)');
    imgGrad.addColorStop(1, 'rgba(255,255,255,0.04)');
    ctx.fillStyle = imgGrad;
    drawRoundRect(ctx, 60, 330, 630, 280, 24);
    ctx.fill();

    // 绘制产品图片
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise<void>((resolve) => {
        img.onload = () => {
          ctx.save();
          drawRoundRect(ctx, 60, 330, 630, 280, 24);
          ctx.clip();
          ctx.drawImage(img, 60, 330, 630, 280);
          ctx.restore();
          resolve();
        };
        img.onerror = () => resolve();
        img.src = '';
      });
    } catch {
      // 图片加载失败时用渐变替代
    }

    // 图片上叠加渐变遮罩
    const overlayGrad = ctx.createLinearGradient(0, 330, 0, 610);
    overlayGrad.addColorStop(0, 'rgba(6,78,59,0.2)');
    overlayGrad.addColorStop(1, 'rgba(6,78,59,0.5)');
    ctx.fillStyle = overlayGrad;
    drawRoundRect(ctx, 60, 330, 630, 280, 24);
    ctx.fill();

    // 图片区域文字
    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    ctx.font = 'bold 40px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('优质护发 · 专业品质', POSTER_W / 2, 490);
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.font = '26px sans-serif';
    ctx.fillText('源自天然 · 科学配方', POSTER_W / 2, 540);

    // ── 邀请人信息卡片 ──
    ctx.fillStyle = 'rgba(255,255,255,0.1)';
    drawRoundRect(ctx, 60, 650, 630, 120, 20);
    ctx.fill();

    // 头像圆圈
    ctx.beginPath();
    ctx.arc(130, 710, 36, 0, Math.PI * 2);
    const avatarGrad = ctx.createLinearGradient(94, 674, 166, 746);
    avatarGrad.addColorStop(0, '#34d399');
    avatarGrad.addColorStop(1, '#059669');
    ctx.fillStyle = avatarGrad;
    ctx.fill();

    // 头像文字
    const avatarChar = (nickname || '用')[0].toUpperCase();
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 32px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(avatarChar, 130, 722);

    // 邀请人名字
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 30px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(nickname || '法之源会员', 185, 700);

    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.font = '24px sans-serif';
    ctx.fillText('邀请您成为团队成员', 185, 735);

    // ── 邀请码卡片 ──
    ctx.fillStyle = '#ffffff';
    drawRoundRect(ctx, 60, 800, 630, 200, 24);
    ctx.fill();

    ctx.fillStyle = '#064e3b';
    ctx.font = 'bold 28px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('我的专属邀请码', POSTER_W / 2, 850);

    // 邀请码背景
    const codeGrad = ctx.createLinearGradient(120, 870, 630, 940);
    codeGrad.addColorStop(0, '#ecfdf5');
    codeGrad.addColorStop(1, '#d1fae5');
    ctx.fillStyle = codeGrad;
    drawRoundRect(ctx, 120, 870, 510, 80, 16);
    ctx.fill();

    // 邀请码文字
    ctx.fillStyle = '#059669';
    ctx.font = 'bold 56px monospace';
    ctx.textAlign = 'center';
    ctx.letterSpacing = '8px';
    ctx.fillText(inviteCode, POSTER_W / 2, 928);
    ctx.letterSpacing = '0px';

    ctx.fillStyle = '#6b7280';
    ctx.font = '22px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('注册时填写邀请码，加入我的团队', POSTER_W / 2, 980);

    // ── 底部步骤说明 ──
    const stepItems = [
      { icon: '①', text: '扫码/复制链接' },
      { icon: '②', text: '填写邀请码注册' },
      { icon: '③', text: '加入团队成长' },
    ];
    const stepY = 1060;
    const stepW = 180;
    const startX = (POSTER_W - stepW * 3 - 30) / 2;

    stepItems.forEach((step, i) => {
      const sx = startX + i * (stepW + 15);
      ctx.fillStyle = 'rgba(255,255,255,0.08)';
      drawRoundRect(ctx, sx, stepY, stepW, 90, 14);
      ctx.fill();

      ctx.fillStyle = '#34d399';
      ctx.font = 'bold 28px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(step.icon, sx + stepW / 2, stepY + 38);

      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      ctx.font = '20px sans-serif';
      ctx.fillText(step.text, sx + stepW / 2, stepY + 70);
    });

    // ── 底部品牌 ──
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.font = '20px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('法之源 · 专业护发品牌', POSTER_W / 2, POSTER_H - 30);

    // 导出图片
    const url = canvas.toDataURL('image/png');
    setPosterUrl(url);
    setGenerating(false);
  }, [inviteCode, nickname]);

  useEffect(() => {
    drawPoster();
  }, [drawPoster]);

  const handleSave = () => {
    const link = document.createElement('a');
    link.download = `法之源邀请海报_${inviteCode}.png`;
    link.href = posterUrl;
    link.click();
    setSaveHint(true);
    setTimeout(() => setSaveHint(false), 3000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70" onClick={onClose}>
      <div
        className="bg-white w-full max-w-[480px] rounded-t-3xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: '92vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 text-base">分享海报</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center cursor-pointer text-gray-400">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Canvas (hidden, for drawing) */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Poster Preview */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(92vh - 140px)' }}>
          <div className="px-5 py-4">
            {generating ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="w-10 h-10 border-3 border-emerald-400 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-sm text-gray-500">正在生成海报...</p>
              </div>
            ) : (
              <div className="rounded-2xl overflow-hidden shadow-sm">
                <img
                  src={posterUrl}
                  alt="邀请海报"
                  className="w-full"
                  style={{ display: 'block' }}
                />
              </div>
            )}

            {saveHint && (
              <div className="mt-3 bg-emerald-50 text-emerald-700 text-sm text-center py-2.5 rounded-xl">
                海报已保存，长按图片可分享到朋友圈
              </div>
            )}

            <p className="text-xs text-gray-400 text-center mt-3">
              长按图片保存到相册，分享给好友
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 pb-6 pt-3 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 border border-gray-200 text-gray-600 rounded-xl py-3 text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            关闭
          </button>
          <button
            onClick={handleSave}
            disabled={generating}
            className="flex-1 bg-emerald-500 text-white rounded-xl py-3 text-sm font-semibold cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <i className="ri-download-line" />
            保存海报
          </button>
        </div>
      </div>
    </div>
  );
};

export default PosterModal;
